import { effect, inject, Injectable } from '@angular/core';

import { ToastService } from '@ui/services/toast.service';

import { AuthService } from './auth.service';

const WARNING_AFTER_MS = 25 * 60 * 1000;
const LOGOUT_AFTER_MS = 30 * 60 * 1000;
const ACTIVITY_EVENTS = ['click', 'keydown', 'mousemove', 'scroll', 'touchstart'] as const;

@Injectable({ providedIn: 'root' })
export class IdleSessionService {
  private readonly auth = inject(AuthService);
  private readonly toast = inject(ToastService);

  private warningTimer: number | null = null;
  private logoutTimer: number | null = null;
  private warningToastId: string | null = null;
  private listening = false;

  constructor() {
    effect(() => {
      if (this.auth.isAuthenticated()) {
        this.start();
      } else {
        this.stop();
      }
    });
  }

  private start(): void {
    if (typeof window === 'undefined') {
      return;
    }
    if (!this.listening) {
      for (const eventName of ACTIVITY_EVENTS) {
        window.addEventListener(eventName, this.handleActivity, { passive: true });
      }
      this.listening = true;
    }
    this.resetTimers();
  }

  private stop(): void {
    if (typeof window !== 'undefined' && this.listening) {
      for (const eventName of ACTIVITY_EVENTS) {
        window.removeEventListener(eventName, this.handleActivity);
      }
    }
    this.listening = false;
    this.clearTimers();
    this.dismissWarning();
  }

  private readonly handleActivity = (): void => {
    if (!this.auth.isAuthenticated()) {
      this.stop();
      return;
    }
    this.resetTimers();
  };

  private resetTimers(): void {
    this.clearTimers();
    this.dismissWarning();
    this.warningTimer = window.setTimeout(() => this.showWarning(), WARNING_AFTER_MS);
    this.logoutTimer = window.setTimeout(() => this.forceLogout(), LOGOUT_AFTER_MS);
  }

  private clearTimers(): void {
    if (this.warningTimer !== null) {
      window.clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
    if (this.logoutTimer !== null) {
      window.clearTimeout(this.logoutTimer);
      this.logoutTimer = null;
    }
  }

  private showWarning(): void {
    if (!this.auth.isAuthenticated() || this.warningToastId) {
      return;
    }
    this.warningToastId = this.toast.show({
      type: 'warning',
      message: 'You will be signed out in 5 minutes due to inactivity.',
      durationMs: LOGOUT_AFTER_MS - WARNING_AFTER_MS,
    });
  }

  private forceLogout(): void {
    if (!this.auth.isAuthenticated()) {
      this.stop();
      return;
    }
    this.dismissWarning();
    this.auth.logout();
  }

  private dismissWarning(): void {
    if (!this.warningToastId) {
      return;
    }
    this.toast.dismiss(this.warningToastId);
    this.warningToastId = null;
  }
}
